import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import moment from 'moment-timezone'
import store from '@/store'
import ability from '@/libs/acl/ability'
import { debounce } from 'lodash'
import useFilterCounter from '@/components/Filters/useFiltersCount'

export default function useList() {
  const refListTable = ref(null)

  const toast = useToast()

  store.dispatch('tenant/getAll')

  const tenantOptions = computed(() => store.getters['tenant/getSelectedItems'])
  const tenantFilter = ref(null)

  store.dispatch('type_alarm/getAll')

  const typeAlarmOptions = computed(() => store.getters['type_alarm/getSelectedItems'])
  const typeAlarmFilter = ref(null)

  const tableColumns = [
    { label: 'ID', key: 'id', sortable: true },
    { label: 'alarm_level.level', key: 'level', sortable: true },
    { label: 'alarm_level.color', key: 'color', sortable: true },
    { label: 'alarm_level.border_color', key: 'border_color', sortable: true },
    { label: 'alarm_level.description', key: 'description', sortable: true },
    { label: 'tenants.title', key: 'tenant.name', sortable: true },
    {
      label: 'types_alarms.title', key: 'alarmTypes', sortable: false,
      formatter: (value, key, item) => {
        if (item.alarmTypes) return [...new Set(item.alarmTypes.map(item => { return item.description }))].join(', ')
        return ""
      }
    },
    { label: 'alarm_level.media', key: 'path', sortable: true },
  ]

  if (ability.can('update', 'AlarmLevel') || ability.can('delete', 'AlarmLevel')) {
    tableColumns.splice(0, 0, { label: 'actions.name', key: 'actions' })
  }

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const action = ref('list')

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })

  const fetchList = (ctx, callback) => {
    store.dispatch('alarm_level/getAll', {
      q: searchQuery.value,
      max: perPage.value,
      'page[n]': currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      id_tenant: tenantFilter.value,
      id_type_alarm: typeAlarmFilter.value,
      action: action.value
    })
      .then(response => {
        if (action.value == 'export') {
          action.value = 'list'
        } else {
          callback(response.data)
          totalList.value = response.meta.total
        }
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error obteniendo la información',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const formatDateAssigned = value => {
    const formattedDate = moment(value).format('DD/MM/YYYY HH:mm:ss')
    return formattedDate
  }

  const refetchData = () => {
    refListTable.value.refresh()
  }

  watch([currentPage, perPage], () => {
    refetchData()
  })

  watch([searchQuery], debounce(() => {
    refetchData()
  }, 500))

  // filters counter ============================
  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      id_tenant: tenantFilter.value,
      id_type_alarm: typeAlarmFilter.value,
    };
    refetchData();
  }
  const clearSearchFilters = () => {
    tenantFilter.value = null;
    typeAlarmFilter.value = null;
    buildFilters();
  }
  // ============================================

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    formatDateAssigned,

    refetchData,

    tenantOptions,
    tenantFilter,
    typeAlarmOptions,
    typeAlarmFilter,
    action,
    filtersCount,
    buildFilters,
    clearSearchFilters,
  }
}
